import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { billingAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Shield, ArrowLeft, User, Globe, CreditCard
} from 'lucide-react';

export default function Settings() {
  const { user, logout, refreshUser } = useAuth();
  const { t, language, changeLanguage } = useTranslation();
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        // Refresh user data to get latest billing dates from server
        await refreshUser();
        const res = await billingAPI.subscription();
        setSubscription(res.data);
      } catch { /* ignore */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpgrade = (planId = 'auditor') => {
    // Redirect to pricing section on landing (like Dashboard) so user can choose plan and scroll
    window.location.href = '/#pricing';
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="settings-page">
      <header className="border-b border-white/5 bg-[#050505]/90 backdrop-blur-lg">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center gap-3">
          <Link to="/dashboard" className="text-neutral-400 hover:text-white" data-testid="settings-back-link">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          </Link>
          <Shield className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
          <span className="font-heading text-lg">{t('settings.title')}</span>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* Account */}
        <div className="border border-white/10 bg-[#0A0A0A] rounded-sm p-6" data-testid="account-section">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-4 h-4 text-blue-600" strokeWidth={1.5} />
            <h3 className="font-heading text-lg text-white">{t('settings.account')}</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-neutral-500 text-sm font-body">{t('settings.name')}</span>
              <span className="text-white text-sm font-body">{user?.name || '-'}</span>
            </div>
            <Separator className="bg-white/5" />
            <div className="flex justify-between items-center">
              <span className="text-neutral-500 text-sm font-body">{t('settings.email')}</span>
              <span className="text-white text-sm font-body">{user?.email || '-'}</span>
            </div>
            <Separator className="bg-white/5" />
            <div className="flex justify-between items-center">
              <span className="text-neutral-500 text-sm font-body">{t('settings.audits_used')}</span>
              <span className="text-white text-sm font-mono">{user?.audits_used || 0}</span>
            </div>
          </div>
        </div>

        {/* Language */}
        <div className="border border-white/10 bg-[#0A0A0A] rounded-sm p-6" data-testid="language-section">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-4 h-4 text-blue-600" strokeWidth={1.5} />
            <h3 className="font-heading text-lg text-white">{t('settings.language')}</h3>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => changeLanguage('en')}
              variant={language === 'en' ? 'default' : 'outline'}
              className={`text-xs font-body tracking-widest h-9 rounded-sm ${
                language === 'en' ? 'bg-blue-600 text-white' : 'border-white/20 text-neutral-400 hover:bg-white/5'
              }`}
              data-testid="lang-en-button"
            >
              {t('settings.en')}
            </Button>
            <Button
              onClick={() => changeLanguage('fr')}
              variant={language === 'fr' ? 'default' : 'outline'}
              className={`text-xs font-body tracking-widest h-9 rounded-sm ${
                language === 'fr' ? 'bg-blue-600 text-white' : 'border-white/20 text-neutral-400 hover:bg-white/5'
              }`}
              data-testid="lang-fr-button"
            >
              {t('settings.fr')}
            </Button>
          </div>
        </div>

        {/* Subscription */}
        <div className="border border-white/10 bg-[#0A0A0A] rounded-sm p-6" data-testid="subscription-section">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="w-4 h-4 text-blue-600" strokeWidth={1.5} />
            <h3 className="font-heading text-lg text-white">{t('settings.subscription')}</h3>
          </div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-neutral-500 text-sm font-body">{t('dash.plan')}</span>
            <Badge className={user?.plan === 'firm' ? 'bg-blue-600 text-white' : user?.plan === 'auditor' ? 'bg-blue-600/70 text-white' : 'bg-neutral-800 text-neutral-300'}>
              {user?.plan === 'firm' ? t('dash.firm') : user?.plan === 'auditor' ? t('dash.auditor') : t('dash.free')}
            </Badge>
          </div>
          {(user?.plan === 'auditor' || user?.plan === 'firm') && (
            <>
              <Separator className="bg-white/5" />
              <div className="flex items-center justify-between py-2 mb-4">
                <span className="text-neutral-500 text-sm font-body">{t('settings.next_billing')}</span>
                <span className="text-white text-sm font-body">
                  {(() => {
                    if (!user?.billing_renews_at) return language === 'fr' ? 'Calcul en cours...' : 'Computing...';
                    try {
                      const d = new Date(user.billing_renews_at);
                      return isNaN(d.getTime()) ? (language === 'fr' ? 'Calcul en cours...' : 'Computing...') : d.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
                    } catch { return language === 'fr' ? 'Calcul en cours...' : 'Computing...'; }
                  })()}
                </span>
              </div>
            </>
          )}
          {user?.plan === 'free' && (
            <div className="space-y-2">
              <Button onClick={handleUpgrade}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-body tracking-widest h-9 rounded-sm w-full"
                data-testid="upgrade-pro-button">
                {t('dash.auditor')} — {language === 'fr' ? '99\u20ac' : '$99'}/mo
              </Button>
              <Button onClick={() => handleUpgrade('firm')}
                variant="outline"
                className="border-white/20 text-neutral-300 hover:bg-white/5 text-xs font-body tracking-widest h-9 rounded-sm w-full"
                data-testid="upgrade-firm-button">
                {t('dash.firm')} — {language === 'fr' ? '249\u20ac' : '$249'}/mo
              </Button>
            </div>
          )}

          {/* Recent transactions */}
          {subscription?.transactions?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/5">
              <p className="text-neutral-500 text-xs font-body mb-2 uppercase tracking-wider">Recent Payments</p>
              {subscription.transactions.map((txn, i) => (
                <div key={i} className="flex items-center justify-between py-2">
                  <span className="text-neutral-400 text-xs font-body">{new Date(txn.created_at).toLocaleDateString()}</span>
                  <span className="text-neutral-400 text-xs font-mono">${txn.amount}</span>
                  <Badge className={txn.payment_status === 'paid' ? 'bg-green-600/20 text-green-400' : 'bg-yellow-600/20 text-yellow-400'}>
                    {txn.payment_status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Logout */}
        <Button
          onClick={logout}
          variant="outline"
          className="w-full border-red-600/30 text-red-400 hover:bg-red-600/10 text-xs font-body tracking-widest h-10 rounded-sm"
          data-testid="settings-logout-button"
        >
          {t('settings.logout')}
        </Button>
      </div>
    </div>
  );
}
